import { useMemo, useRef, useState } from 'react'
import { router } from '@inertiajs/react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  SCHOOL_FEATURES,
  featuresByGroup,
  type CustomSchoolFeature,
  type SchoolFeatureGroup,
} from '@/lib/schoolFeatures'
import { IconPicker } from '@/components/ui/icon-picker'
import { BUSINESS_CATEGORIES } from '@/lib/businessCategories'
import type { BusinessCategory } from '@/components/public/CreateYourPage'
import {
  Globe,
  Copy,
  ExternalLink,
  UserPlus,
  Package,
  DollarSign,
  Clock,
  MapPin,
  ImagePlus,
  X,
  AlertTriangle,
  LayoutGrid,
  Type,
  Award,
  Users,
  MessageCircle,
  Sparkles,
  Plus,
  Trash2,
} from 'lucide-react'

export interface PublicSchool {
  slug: string | null
  phone: string | null
  business_category: BusinessCategory
  /** Refino opcional dentro da categoria (ex.: Kitesurf sob Surf & Esportes). */
  explore_subcategory_id: number | null
  public_page_enabled: boolean
  public_registration_enabled: boolean
  public_show_packages: boolean
  public_show_package_prices: boolean
  public_show_hours: boolean
  public_show_map: boolean
  public_show_teachers: boolean
  public_show_whatsapp: boolean
  public_highlight_trial: boolean
  public_headline: string | null
  public_tagline: string | null
  public_about: string | null
  public_features: string[]
  public_custom_features: CustomSchoolFeature[]
}

interface GalleryImage {
  id: number
  url: string
}

/**
 * Mesmo teto da Cloudinary na conta do app — checar aqui evita subir um arquivo
 * grande inteiro só para o servidor rejeitar depois (ver `SettingsController`,
 * que aplica o mesmo limite: é a validação que garante, o cliente é só a resposta
 * rápida).
 */
const GALLERY_MAX_SIZE_MB = 10
const GALLERY_MAX_SIZE_BYTES = GALLERY_MAX_SIZE_MB * 1024 * 1024
const GALLERY_ACCEPTED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
]

/** Uma subcategoria do catálogo, achatada com a key da categoria dona — é o
 *  suficiente para o select filtrar sem precisar da árvore inteira. */
export interface SubcategoryOption {
  id: number
  label: string
  category_key: BusinessCategory
}

interface PublicPageSettingsProps {
  school: PublicSchool
  gallery: GalleryImage[]
  hasTrialPackage: boolean
  /** Catálogo completo de subcategorias das 7 categorias. */
  subcategories: SubcategoryOption[]
}

/** Sentinela do "sem especialidade" no Select — o Radix trata `value=""` como
 *  "nenhum item selecionado" e não dispara onValueChange. */
const NO_SUBCATEGORY = 'none'

// Gera um id simples para o selo custom (sem dependência externa).
const newFeatureId = (): string =>
  `cf_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`

export function PublicPageSettings({
  school,
  gallery,
  hasTrialPackage,
  subcategories,
}: PublicPageSettingsProps) {
  const [pageEnabled, setPageEnabled] = useState(school.public_page_enabled)
  // `?? 'esportes'` cobre escolas de antes da coluna existir (default do
  // banco, e onde a migration ReplaceExploreTaxonomy aterrissou as antigas).
  const [businessCategory, setBusinessCategory] = useState<BusinessCategory>(
    school.business_category ?? 'esportes',
  )
  const [subcategoryId, setSubcategoryId] = useState<string>(
    school.explore_subcategory_id ? String(school.explore_subcategory_id) : '',
  )

  // As subcategorias da categoria escolhida. Trocar de categoria zera a escolha
  // anterior — o servidor rejeita subcategoria de outra categoria (validação no
  // model School), então deixar a antiga marcada só produziria erro no salvar.
  const subcategoryOptions = useMemo(
    () =>
      subcategories.filter(
        (subcategory) => subcategory.category_key === businessCategory,
      ),
    [subcategories, businessCategory],
  )
  const [registrationEnabled, setRegistrationEnabled] = useState(
    school.public_registration_enabled,
  )
  const [showPackages, setShowPackages] = useState(school.public_show_packages)
  const [showPackagePrices, setShowPackagePrices] = useState(
    school.public_show_package_prices,
  )
  const [showHours, setShowHours] = useState(school.public_show_hours)
  const [showMap, setShowMap] = useState(school.public_show_map)
  const [showTeachers, setShowTeachers] = useState(school.public_show_teachers)

  const [showWhatsapp, setShowWhatsapp] = useState(school.public_show_whatsapp)
  const [highlightTrial, setHighlightTrial] = useState(
    school.public_highlight_trial,
  )

  const [headline, setHeadline] = useState(school.public_headline ?? '')
  const [tagline, setTagline] = useState(school.public_tagline ?? '')
  const [about, setAbout] = useState(school.public_about ?? '')
  const [features, setFeatures] = useState<string[]>(
    school.public_features ?? [],
  )
  const [customFeatures, setCustomFeatures] = useState<CustomSchoolFeature[]>(
    school.public_custom_features ?? [],
  )

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isUploading, setIsUploading] = useState(false)

  const fileInputRef = useRef<HTMLInputElement>(null)

  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  const publicUrl = school.slug ? `${origin}/explore/${school.slug}` : null

  const toggleFeature = (key: string) => {
    setFeatures((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    )
  }

  const addCustomFeature = (group: SchoolFeatureGroup) => {
    setCustomFeatures((prev) => [
      ...prev,
      { id: newFeatureId(), group, label: '', icon: 'Sparkles' },
    ])
  }

  const updateCustomFeature = (
    id: string,
    patch: Partial<CustomSchoolFeature>,
  ) => {
    setCustomFeatures((prev) =>
      prev.map((f) => (f.id === id ? { ...f, ...patch } : f)),
    )
  }

  const removeCustomFeature = (id: string) => {
    setCustomFeatures((prev) => prev.filter((f) => f.id !== id))
  }

  const handleSave = () => {
    // Ignora selos custom sem nome (evita lixo no banco / ícones vazios na landing).
    const cleanCustomFeatures = customFeatures
      .map((f) => ({ ...f, label: f.label.trim() }))
      .filter((f) => f.label.length > 0)

    setIsSubmitting(true)
    router.patch(
      '/settings',
      {
        school: {
          public_page_enabled: pageEnabled,
          business_category: businessCategory,
          explore_subcategory_id:
            subcategoryId === '' ? null : Number(subcategoryId),
          public_registration_enabled: registrationEnabled,
          public_show_packages: showPackages,
          public_show_package_prices: showPackagePrices,
          public_show_hours: showHours,
          public_show_map: showMap,
          public_show_teachers: showTeachers,
          public_show_whatsapp: showWhatsapp,
          public_highlight_trial: highlightTrial,
          public_headline: headline,
          public_tagline: tagline,
          public_about: about,
          public_features: features,
          public_custom_features: cleanCustomFeatures,
        },
      },
      {
        onSuccess: () => toast.success('Página pública atualizada!'),
        onError: () => toast.error('Erro ao salvar a página pública'),
        onFinish: () => setIsSubmitting(false),
      },
    )
  }

  const copyLink = () => {
    if (!publicUrl) return
    navigator.clipboard
      .writeText(publicUrl)
      .then(() => toast.success('Link copiado!'))
      .catch(() => toast.error('Não foi possível copiar o link'))
  }

  const handleFilesSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])
    if (files.length === 0) return

    // Filtra antes de subir: tipo ou tamanho errado não sobrevive à validação do
    // servidor mesmo, e mandar mesmo assim só gasta o upload inteiro para um
    // arquivo que já sabemos que vai voltar rejeitado.
    const valid = files.filter((file) => {
      if (!GALLERY_ACCEPTED_TYPES.includes(file.type)) {
        toast.error(`${file.name}: envie apenas JPEG, PNG, WebP ou GIF.`)
        return false
      }
      if (file.size > GALLERY_MAX_SIZE_BYTES) {
        toast.error(
          `${file.name}: imagem muito grande (máximo ${GALLERY_MAX_SIZE_MB}MB).`,
        )
        return false
      }
      return true
    })

    if (fileInputRef.current) fileInputRef.current.value = ''
    if (valid.length === 0) return

    setIsUploading(true)
    router.post(
      '/settings/public_gallery',
      { images: valid },
      {
        forceFormData: true,
        onSuccess: () => toast.success('Imagens adicionadas!'),
        onError: (errors) =>
          toast.error(Object.values(errors)[0] || 'Erro ao enviar imagens'),
        onFinish: () => setIsUploading(false),
      },
    )
  }

  const removeImage = (id: number) => {
    router.delete(`/settings/public_gallery/${id}`, {
      onSuccess: () => toast.success('Imagem removida'),
      onError: () => toast.error('Erro ao remover imagem'),
    })
  }

  return (
    <div className="space-y-6 mt-6">
      {/* Ativação da página pública */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Página Pública da Escola
          </CardTitle>
          <CardDescription className="text-sm">
            Uma página aberta na internet com a identidade da sua escola, onde
            qualquer pessoa pode conhecer e se cadastrar como aluno.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <p className="text-sm font-medium">Ativar página pública</p>
              <p className="text-sm text-muted-foreground">
                Quando desligada, o link fica indisponível e ninguém consegue
                acessar a página.
              </p>
            </div>
            <Switch checked={pageEnabled} onCheckedChange={setPageEnabled} />
          </div>

          {pageEnabled && (
            <>
              {publicUrl ? (
                <div className="rounded-lg border bg-muted/40 p-3">
                  <p className="mb-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Link da sua página
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <code className="flex-1 min-w-0 truncate rounded bg-background px-2 py-1.5 text-sm">
                      {publicUrl}
                    </code>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={copyLink}
                      className="gap-1.5"
                    >
                      <Copy className="h-3.5 w-3.5" />
                      Copiar
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      asChild
                      className="gap-1.5"
                    >
                      <a
                        href={publicUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                        Abrir
                      </a>
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                  <p>
                    Sua escola ainda não tem um identificador (slug) para a URL.
                    Defina um em{' '}
                    <a
                      href="/school_profile/edit"
                      className="font-medium underline"
                    >
                      Perfil da Escola
                    </a>{' '}
                    para gerar o link público.
                  </p>
                </div>
              )}

              {/* Nicho: define em qual filtro do BeWave Explore a página
                  aparece. Existe porque nem todo cliente é escola de surf —
                  há academia, pousada etc. — e sem isto toda escola ficava
                  presa no default "Surf". */}
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                <div className="space-y-1">
                  <p className="text-sm font-medium flex items-center gap-2">
                    <LayoutGrid className="h-4 w-4" />
                    Nicho do negócio
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Em qual categoria sua página aparece no filtro do BeWave
                    Explore.
                  </p>
                </div>
                <Select
                  value={businessCategory}
                  onValueChange={(value) => {
                    setBusinessCategory(value as BusinessCategory)
                    // Zera a especialidade: ela pertencia à categoria anterior,
                    // e o model School rejeita a combinação.
                    setSubcategoryId('')
                  }}
                >
                  <SelectTrigger
                    aria-label="Nicho do negócio"
                    className="w-full sm:w-56"
                  >
                    <SelectValue placeholder="Selecione o nicho" />
                  </SelectTrigger>
                  <SelectContent>
                    {BUSINESS_CATEGORIES.map(({ key, label, icon: Icon }) => (
                      <SelectItem key={key} value={key}>
                        <span className="flex items-center gap-2">
                          <Icon aria-hidden="true" className="h-4 w-4" />
                          {label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Especialidade: o segundo nível do filtro do Explore. Opcional
                  de propósito — quem não declara continua aparecendo na
                  categoria, e só some quando alguém desce um nível. */}
              {subcategoryOptions.length > 0 && (
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                  <div className="space-y-1">
                    <p className="text-sm font-medium flex items-center gap-2">
                      <Sparkles className="h-4 w-4" />
                      Especialidade (opcional)
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Refina dentro do nicho. Sem ela sua página aparece só no
                      filtro da categoria, nunca no da especialidade.
                    </p>
                  </div>
                  <Select
                    value={
                      subcategoryId === '' ? NO_SUBCATEGORY : subcategoryId
                    }
                    onValueChange={(value) =>
                      setSubcategoryId(value === NO_SUBCATEGORY ? '' : value)
                    }
                  >
                    <SelectTrigger
                      aria-label="Especialidade"
                      className="w-full sm:w-56"
                    >
                      <SelectValue placeholder="Nenhuma" />
                    </SelectTrigger>
                    <SelectContent>
                      {/* O Radix Select não aceita "" como value de item, daí a
                          sentinela — "" ali fecharia o menu sem selecionar. */}
                      <SelectItem value={NO_SUBCATEGORY}>Nenhuma</SelectItem>
                      {subcategoryOptions.map(({ id, label }) => (
                        <SelectItem key={id} value={String(id)}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <p className="text-sm font-medium flex items-center gap-2">
                    <UserPlus className="h-4 w-4" />
                    Permitir cadastro de alunos pela página
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Exibe o botão "Cadastre-se". A pessoa cria a conta com a
                    própria senha e já entra como aluno da sua escola.
                  </p>
                </div>
                <Switch
                  checked={registrationEnabled}
                  onCheckedChange={setRegistrationEnabled}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {pageEnabled && (
        <>
          {/* Textos da página */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Type className="h-5 w-5" />
                Textos da página
              </CardTitle>
              <CardDescription className="text-sm">
                Personalize o que aparece no topo e na apresentação da escola.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="public-headline">Título</Label>
                <Input
                  id="public-headline"
                  value={headline}
                  onChange={(e) => setHeadline(e.target.value)}
                  placeholder="Ex: Escola de Surf Onda Azul (vazio usa o nome da escola)"
                  maxLength={80}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="public-tagline">Frase de destaque</Label>
                <Input
                  id="public-tagline"
                  value={tagline}
                  onChange={(e) => setTagline(e.target.value)}
                  placeholder="Ex: Aulas de surf em Fortaleza há 10 anos"
                  maxLength={120}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="public-about">Sobre a escola</Label>
                <Textarea
                  id="public-about"
                  value={about}
                  onChange={(e) => setAbout(e.target.value)}
                  placeholder="Conte a história da escola, diferenciais, estrutura, experiência..."
                  rows={5}
                />
              </div>
            </CardContent>
          </Card>

          {/* Selos e facilidades */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Award className="h-5 w-5" />
                Selos e facilidades
              </CardTitle>
              <CardDescription className="text-sm">
                Marque o que a escola oferece. Cada item vira um ícone na página
                pública.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Níveis de aula
                </p>
                <div className="flex flex-wrap gap-2">
                  {featuresByGroup('level').map((feature) => (
                    <FeatureToggle
                      key={feature.key}
                      feature={feature}
                      active={features.includes(feature.key)}
                      onToggle={() => toggleFeature(feature.key)}
                    />
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Facilidades
                </p>
                <div className="flex flex-wrap gap-2">
                  {featuresByGroup('amenity').map((feature) => (
                    <FeatureToggle
                      key={feature.key}
                      feature={feature}
                      active={features.includes(feature.key)}
                      onToggle={() => toggleFeature(feature.key)}
                    />
                  ))}
                </div>
              </div>

              {/* Selos criados pela escola (nome + ícone livres) */}
              <div className="space-y-4 border-t pt-4">
                <p className="text-sm font-medium">Criar os seus próprios</p>
                <CustomFeatureGroup
                  label="Níveis de aula personalizados"
                  group="level"
                  items={customFeatures.filter((f) => f.group === 'level')}
                  onAdd={() => addCustomFeature('level')}
                  onUpdate={updateCustomFeature}
                  onRemove={removeCustomFeature}
                />
                <CustomFeatureGroup
                  label="Facilidades personalizadas"
                  group="amenity"
                  items={customFeatures.filter((f) => f.group === 'amenity')}
                  onAdd={() => addCustomFeature('amenity')}
                  onUpdate={updateCustomFeature}
                  onRemove={removeCustomFeature}
                />
              </div>
            </CardContent>
          </Card>

          {/* Seções */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Seções da página</CardTitle>
              <CardDescription className="text-sm">
                Escolha quais blocos aparecem para os visitantes.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <ToggleRow
                icon={<Package className="h-4 w-4" />}
                title="Pacotes disponíveis"
                description="Lista os pacotes ativos da escola."
                checked={showPackages}
                onCheckedChange={setShowPackages}
              />
              {showPackages && (
                <div className="ml-6 border-l pl-4">
                  <ToggleRow
                    icon={<DollarSign className="h-4 w-4" />}
                    title="Mostrar preços"
                    description={
                      'Se desligado, os pacotes aparecem com "valor sob consulta".'
                    }
                    checked={showPackagePrices}
                    onCheckedChange={setShowPackagePrices}
                  />
                </div>
              )}
              <ToggleRow
                icon={<Users className="h-4 w-4" />}
                title="Nosso time"
                description="Mostra os professores da escola."
                checked={showTeachers}
                onCheckedChange={setShowTeachers}
              />
              <ToggleRow
                icon={<Clock className="h-4 w-4" />}
                title="Horário de funcionamento"
                description="Mostra os dias e horários em que a escola funciona."
                checked={showHours}
                onCheckedChange={setShowHours}
              />
              <ToggleRow
                icon={<MapPin className="h-4 w-4" />}
                title="Localização no mapa"
                description="Exibe o mapa com o endereço da escola (requer coordenadas no Perfil da Escola)."
                checked={showMap}
                onCheckedChange={setShowMap}
              />
              <div className="space-y-2">
                <ToggleRow
                  icon={<MessageCircle className="h-4 w-4" />}
                  title="Botão WhatsApp"
                  description="Exibe o botão “Falar no WhatsApp” usando o telefone da escola."
                  checked={showWhatsapp}
                  onCheckedChange={setShowWhatsapp}
                />
                {showWhatsapp && !school.phone && (
                  <div className="ml-6 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-2.5 text-xs">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
                    <p>
                      A escola ainda não tem telefone cadastrado. Defina em{' '}
                      <a
                        href="/school_profile/edit"
                        className="font-medium underline"
                      >
                        Perfil da Escola
                      </a>{' '}
                      para o botão funcionar.
                    </p>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <ToggleRow
                  icon={<Sparkles className="h-4 w-4" />}
                  title="Destacar aula experimental"
                  description="Exibe em destaque o pacote de aula experimental no topo da página."
                  checked={highlightTrial && hasTrialPackage}
                  onCheckedChange={setHighlightTrial}
                  disabled={!hasTrialPackage}
                />
                {!hasTrialPackage && (
                  <div className="ml-6 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-2.5 text-xs">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
                    <p>
                      Nenhum pacote marcado como aula experimental. Crie um
                      pacote com “gera lead de aquisição” em{' '}
                      <a
                        href="/lesson_packages"
                        className="font-medium underline"
                      >
                        Pacotes de Aulas
                      </a>
                      .
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Galeria de imagens */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ImagePlus className="h-5 w-5" />
                Galeria (carrossel)
              </CardTitle>
              <CardDescription className="text-sm">
                Imagens que aparecem no topo da página pública. As alterações
                são salvas na hora.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {gallery.length > 0 && (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                  {gallery.map((image) => (
                    <div
                      key={image.id}
                      className="group relative aspect-video overflow-hidden rounded-lg border"
                    >
                      <img
                        src={image.url}
                        alt="Imagem da galeria"
                        className="h-full w-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(image.id)}
                        className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white opacity-0 transition-opacity group-hover:opacity-100"
                        aria-label="Remover imagem"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleFilesSelected}
              />
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className="gap-2"
                >
                  <ImagePlus className="h-4 w-4" />
                  {isUploading ? 'Enviando...' : 'Adicionar imagens'}
                </Button>
                <span className="text-xs text-muted-foreground">
                  JPEG, PNG, WebP ou GIF — até {GALLERY_MAX_SIZE_MB}MB cada
                </span>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={isSubmitting}>
          {isSubmitting ? 'Salvando...' : 'Salvar alterações'}
        </Button>
      </div>
    </div>
  )
}

function FeatureToggle({
  feature,
  active,
  onToggle,
}: {
  feature: (typeof SCHOOL_FEATURES)[number]
  active: boolean
  onToggle: () => void
}) {
  const Icon = feature.icon
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        'flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors',
        active
          ? 'border-primary bg-primary/10 text-primary'
          : 'border-border text-muted-foreground hover:border-primary/40 hover:bg-accent',
      )}
    >
      <Icon className="h-4 w-4" />
      {feature.label}
    </button>
  )
}

function ToggleRow({
  icon,
  title,
  description,
  checked,
  onCheckedChange,
  disabled,
}: {
  icon: React.ReactNode
  title: string
  description: string
  checked: boolean
  onCheckedChange: (value: boolean) => void
  disabled?: boolean
}) {
  return (
    <div
      className={cn(
        'flex items-start justify-between gap-4',
        disabled && 'opacity-60',
      )}
    >
      <div className="space-y-1">
        <p className="text-sm font-medium flex items-center gap-2">
          {icon}
          {title}
        </p>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
      />
    </div>
  )
}

function CustomFeatureGroup({
  label,
  group,
  items,
  onAdd,
  onUpdate,
  onRemove,
}: {
  label: string
  group: SchoolFeatureGroup
  items: CustomSchoolFeature[]
  onAdd: () => void
  onUpdate: (id: string, patch: Partial<CustomSchoolFeature>) => void
  onRemove: (id: string) => void
}) {
  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      {items.length > 0 && (
        <div className="space-y-2">
          {items.map((item) => (
            <div key={item.id} className="flex items-center gap-2">
              <IconPicker
                value={item.icon}
                onChange={(icon) => onUpdate(item.id, { icon })}
              />
              <Input
                value={item.label}
                onChange={(e) => onUpdate(item.id, { label: e.target.value })}
                placeholder={
                  group === 'level'
                    ? 'Ex: Aula de longboard'
                    : 'Ex: Aluguel de roupa de borracha'
                }
                maxLength={40}
                className="h-9"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="shrink-0 text-muted-foreground hover:text-destructive"
                onClick={() => onRemove(item.id)}
                aria-label="Remover selo"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-1.5"
        onClick={onAdd}
      >
        <Plus className="h-3.5 w-3.5" />
        Adicionar
      </Button>
    </div>
  )
}
